/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Monaco completion orchestrator.
 *
 * Single entry point Monaco's `provideCompletionItems` calls. Dispatches to
 * the resolver registered for `engineId`, returns Monaco-shaped suggestions.
 *
 * NOT a class. Pure functional module — Monaco / language-service-provider
 * dependencies flow through `deps`, never imported directly.
 *
 * Every user engine routes through its dialect-native resolver registered in
 * `resolverRegistry.ts`. The `mock` engine has its own `mockResolver` that
 * combines a small in-process LSP bridge with the schema bus.
 */

import type { DatabaseEngineId, ScriptScope } from '@/core/engines';
import type { CompletionPositionKind } from '@/core/engines/intellisenseTypes';
import type { LanguageServiceProvider } from '@/features/connections/services/language-services/languageServiceProvider';
import { isCaretInComment } from '@/features/intellisense/context/caretInComment';
import { deriveCompletionContext } from '@/features/intellisense/context/deriveContext';
import {
  applyResolvedKeywordCase,
  resolveKeywordCase,
  type KeywordCasingHint,
  type ResolvedKeywordCase,
} from '@/features/intellisense/ranking/keywordCasing';
import type { RankReport } from '@/features/intellisense/ranking/rank';
import type { CompletionClassifiedBy } from '@/features/intellisense/telemetry/completionTelemetry';
import type { PredicateHeadRole, ValueSlotRole } from '@/features/sql-grammar/model';

import type { OrchestratorResolver } from './types';
import {
  applyVersionGatingPostProcess,
  type VersionGatingPostProcessHint,
} from './versionGatingPostProcess';

export type OrchestratorDeps = {
  readonly engineId: DatabaseEngineId;
  readonly documentUri: string;
  readonly connectionId?: string;
  readonly database?: string;
  readonly languageServiceProvider: LanguageServiceProvider;
  readonly monaco: any;
  readonly statementTextBeforeCursor: string;
  readonly resolverFor: (engineId: DatabaseEngineId) => OrchestratorResolver;
  /**
   * Cross-engine keyword-casing setting from `appStore.intellisenseSettings`.
   * Applied as a post-process on keyword-kind suggestions returned by the
   * resolver so that legacy LSP paths (today) AND future native dialect
   * resolvers (which already pre-apply via `rankCandidates`) both honor the
   * setting. Idempotent — re-applying the same case is a no-op.
   */
  readonly keywordCasing?: KeywordCasingHint;
  /**
   * Version-gating post-process. When set, every catalog-eligible suggestion
   * is checked against `compat`; `unavailable` and `deprecated` items are
   * decorated with a right-aligned badge, demoted in `sortText`, and (for
   * `deprecated`) tagged so Monaco renders them strikethrough. `mode`
   * controls whether unavailable items are dropped (`'strict'`), annotated
   * (`'annotated'`, default UX), or left untouched (`'off'`).
   */
  readonly versionGating?: VersionGatingPostProcessHint;
  /**
   * SQLite-only: forwarded to the resolver as
   * `OrchestratorResolverInput.sqliteRevealShadowTables`. Default false.
   */
  readonly sqliteRevealShadowTables?: boolean;
  /**
   * Script-scope resolution context — forwarded to the resolver. Undefined /
   * empty when the buffer has no scope-changing statement before the cursor;
   * resolvers then fall back to the connection-default `database` / `schema`.
   */
  readonly scriptScope?: ScriptScope;
};

export type OrchestratorResult = {
  readonly suggestions: readonly any[];
  readonly incomplete: boolean;
  /**
   * Value-slot plan T14 — classification passthrough for `completion.resolved`
   * telemetry (`SqlMonaco.tsx` forwards these verbatim). Sourced from the
   * resolver's own `OrchestratorResolverResult` fields when it supplies them,
   * otherwise from `deriveClassificationForTelemetry`'s own (best-effort)
   * `deriveCompletionContext` call below. Low-cardinality only — see the
   * `OrchestratorResolverResult` doc comment for what must never appear here.
   */
  readonly positionKind?: CompletionPositionKind;
  readonly classifiedBy?: CompletionClassifiedBy;
  readonly valueSlotRole?: ValueSlotRole;
  readonly predicateHeadRole?: PredicateHeadRole;
  /** The untyped cap removed candidates (`RankReport.wasTruncated`) — `truncatedCount` telemetry. */
  readonly truncated?: boolean;
};

export type OrchestratorMonacoContext = {
  readonly triggerKind?: number;
  readonly triggerCharacter?: string;
};

export async function provideCompletionItems(
  model: any,
  position: any,
  context: OrchestratorMonacoContext | undefined,
  deps: OrchestratorDeps,
): Promise<OrchestratorResult> {
  // Nothing is completable inside a comment, and offering a list there is
  // actively destructive: with the suggest widget open, Enter accepts an item
  // instead of breaking the line, so typing `-- note⏎BEGIN …` rewrote the
  // comment to `-- noneditionable` and swallowed the newline. Gate before the
  // resolver runs — this is the one place every engine's completions funnel
  // through, so a per-resolver check would be five chances to miss it.
  if (
    isCaretInComment(
      model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      }),
      // `#` starts a line comment only in MySQL. Elsewhere it is a real
      // mid-statement token (PG `#>`/`#>>`/`#-` operators, MSSQL `#temp`
      // tables) and must not suppress completions.
      { hashIsLineComment: deps.engineId === 'mysql' },
    )
  ) {
    return { suggestions: [], incomplete: false };
  }

  const wordInfo = model.getWordUntilPosition(position);
  const currentWordTrimmed = (wordInfo.word || '').trim();
  const statementTrimmed = deps.statementTextBeforeCursor.trimStart();
  const isAtStatementStartOrFirstToken =
    statementTrimmed.length === 0 || /^[a-zA-Z]+$/.test(statementTrimmed);
  const isManualTrigger = context?.triggerKind === 0;
  const shortWordIncomplete =
    !isManualTrigger &&
    isAtStatementStartOrFirstToken &&
    currentWordTrimmed.length > 0 &&
    currentWordTrimmed.length < 2;

  const resolver = deps.resolverFor(deps.engineId);

  const triggerKindNarrowed: 1 | 2 | undefined =
    context?.triggerKind === 1 ? 1 : context?.triggerKind === 2 ? 2 : undefined;

  const rankReport: RankReport = { wasTruncated: false };
  const result = await resolver.resolve({
    model,
    position,
    engineId: deps.engineId,
    documentUri: deps.documentUri,
    connectionId: deps.connectionId,
    database: deps.database,
    languageServiceProvider: deps.languageServiceProvider,
    monaco: deps.monaco,
    triggerKind: triggerKindNarrowed,
    triggerCharacter: context?.triggerCharacter,
    statementTextBeforeCursor: deps.statementTextBeforeCursor,
    sqliteRevealShadowTables: deps.sqliteRevealShadowTables,
    scriptScope: deps.scriptScope ?? {},
    rankReport,
  });

  const cased = applyKeywordCasingPostProcess(
    result.suggestions,
    deps.keywordCasing,
    deps.monaco,
  );
  const suggestions = applyVersionGatingPostProcess(
    cased,
    deps.versionGating,
    deps.monaco,
  );

  // D7 (plan16 T6): the ranking layer caps untyped suggestion lists at
  // `UNTYPED_SUGGESTION_CAP` (post-ranking, top-N quality preserved). When
  // the cap actually cut candidates, the caret is still hunting through a
  // truncated set — tell Monaco to re-query as the user types instead of
  // caching this snapshot as final. 1.5.1 F3: read the ranker's own
  // `wasTruncated`, never the list length — bounded populations are kept
  // past the cap, so MySQL's uncut `SELECT ▮` list (> 500 keywords and
  // built-ins on a small schema) used to re-query on every typed character.
  const cappedUntypedIncomplete = currentWordTrimmed.length === 0 && rankReport.wasTruncated;

  // Value-slot plan T14 — classification passthrough for `completion.resolved`
  // telemetry. Prefer whatever the resolver already surfaced (future
  // resolver-side optimization, avoids a second parse); fall back to our own
  // best-effort `deriveCompletionContext` call so the telemetry pipe carries
  // real data even though no resolver populates these fields yet (that lands
  // with the per-engine flips, T13a–e).
  const fallbackClassification =
    result.positionKind !== undefined && result.classifiedBy !== undefined
      ? undefined
      : deriveClassificationForTelemetry(model, position, deps.engineId, deps.scriptScope ?? {});

  return {
    suggestions,
    incomplete: shortWordIncomplete || cappedUntypedIncomplete,
    positionKind: result.positionKind ?? fallbackClassification?.positionKind,
    classifiedBy: result.classifiedBy ?? fallbackClassification?.classifiedBy,
    valueSlotRole: result.valueSlotRole ?? fallbackClassification?.valueSlotRole,
    predicateHeadRole: result.predicateHeadRole ?? fallbackClassification?.predicateHeadRole,
    truncated: rankReport.wasTruncated,
  };
}

/**
 * Best-effort classification for telemetry only — never lets a parse failure
 * break completion. `deriveCompletionContext` is a pure sync text scan (no
 * I/O), the same function every per-engine resolver already calls
 * internally with the same `(text, offset, engineId, scriptScope)` inputs, so
 * this reproduces the resolver's own classification without touching any
 * resolver file. Returns only the low-cardinality fields telemetry is
 * allowed to see — `ValueSlotInfo.lhs`/`lhsRange` never leave this function.
 */
function deriveClassificationForTelemetry(
  model: any,
  position: any,
  engineId: DatabaseEngineId,
  scriptScope: ScriptScope,
): {
  readonly positionKind?: CompletionPositionKind;
  readonly classifiedBy?: CompletionClassifiedBy;
  readonly valueSlotRole?: ValueSlotRole;
  readonly predicateHeadRole?: PredicateHeadRole;
} {
  try {
    const ctx = deriveCompletionContext(
      model.getValue(),
      model.getOffsetAt(position),
      engineId,
      scriptScope,
    );
    return {
      positionKind: ctx.positionKind,
      classifiedBy: ctx.classifiedBy,
      valueSlotRole: ctx.valueSlot?.role,
      predicateHeadRole: ctx.predicateHead?.role,
    };
  } catch {
    return {};
  }
}

function applyKeywordCasingPostProcess(
  suggestions: readonly any[],
  hint: KeywordCasingHint | undefined,
  monaco: any,
): readonly any[] {
  if (!hint || suggestions.length === 0) return suggestions;
  const monacoKeywordKind = monaco?.languages?.CompletionItemKind?.Keyword;
  const keywordKind = typeof monacoKeywordKind === 'number' ? monacoKeywordKind : -1;
  // Resolved lazily, once per request: the hint is the same for every item,
  // and resolving it can scan the whole buffer (see `resolveKeywordCase`).
  let desired: ResolvedKeywordCase | undefined;
  return suggestions.map((item) => {
    if (!isKeywordItem(item, keywordKind)) return item;
    const rawLabel = typeof item.label === 'string' ? item.label : item.label?.label;
    if (typeof rawLabel !== 'string') return item;
    desired ??= resolveKeywordCase(hint);
    const newLabel = applyResolvedKeywordCase(rawLabel, desired);
    const newInsertText =
      typeof item.insertText === 'string'
        ? applyResolvedKeywordCase(item.insertText, desired)
        : item.insertText;
    const newFilterText =
      typeof item.filterText === 'string'
        ? applyResolvedKeywordCase(item.filterText, desired)
        : item.filterText;
    const labelOut = typeof item.label === 'string'
      ? newLabel
      : { ...item.label, label: newLabel };
    return {
      ...item,
      label: labelOut,
      insertText: newInsertText,
      ...(newFilterText !== undefined ? { filterText: newFilterText } : {}),
    };
  });
}

function isKeywordItem(item: any, keywordKind: number): boolean {
  if (item?.kind === keywordKind) return true;
  // Defensive: detect items the legacy LSP path emits with a "SQL keyword"
  // detail string but a non-Keyword kind (some LSPs use Variable/Text). The
  // detail string is a stable hint maintained across both the SSTS and the
  // mock LSP, so it lets the post-process apply uniformly even when the
  // upstream kind mapping is inconsistent.
  const detail = typeof item?.detail === 'string' ? item.detail : '';
  if (/sql keyword/i.test(detail)) return true;
  return false;
}
